import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { influencers, InfluencerTypes } from 'api';
import { Box, Skeleton, Stack } from '@mui/material';
import { CALENDAR_MONTHS } from 'constants/dateOptions';
import { InfoCard } from 'components';
import ReachClicksChart from './ReachClicksChart';

interface Props {
  data: InfluencerTypes.ItemExtended;
}

const ReachClicksBlock = (props: Props): JSX.Element | null => {
  const { data: influencerData } = props;

  const influencerId = useMemo(() => influencerData._id.$oid, [influencerData]);

  const { data, isLoading } = useQuery(
    [influencers.endpoints.getDealsChartById(influencerId), 'reach_clicks'],
    () => influencers.getDealsChartById(influencerId, 'reach_clicks'),
  );

  const chartData = useMemo(() => {
    if (data) {
      const cd = 'reach_click' in data ? data.reach_click : {};

      const years = Object.keys(cd)
        .map((d) => +d.split(' ')[1])
        .filter((v, i, a) => a.indexOf(v) === i);

      const opt = years
        .map((year) => CALENDAR_MONTHS.map((month) => ({ ...month, year })))
        .flat();

      return opt.map((option) => {
        const key = `${option.monthName} ${option.year}`;

        const actualReach = key in cd ? cd[key].actual_reach : 0;
        const avgOverallReach = key in cd ? cd[key].avg_overall_reach : 0;
        const totalClicks = key in cd ? cd[key].total_clicks : 0;

        return {
          ...option,
          date: key,
          actualReach,
          avgOverallReach,
          totalClicks,
        };
      });
    }

    return [];
  }, [data]);

  return (
    <InfoCard title="Reach & clicks insights">
      <Stack p={3}>
        {isLoading ? (
          <Box mt={3}>
            <Skeleton
              variant="rectangular"
              height={`${400 - 24}px`}
              sx={(t) => ({ borderRadius: `${t.shape.borderRadius}px` })}
            />
          </Box>
        ) : (
          <ReachClicksChart data={chartData} height={400} />
        )}
      </Stack>
    </InfoCard>
  );
};

export default ReachClicksBlock;
